// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  admin/src/pages/Coupons.jsx  —  Quickry Admin
//  Copy to: C:\quickry\admin\src\pages\Coupons.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect } from "react";
import { api } from "../utils/api";
import { Plus, Tag, Check, X, Loader2, ToggleLeft, ToggleRight, Trash2, RefreshCw, Store, Globe } from "lucide-react";

const T = {
  primary:"#f97316", primaryDark:"#ea580c", primaryLight:"#fff7ed", primaryMid:"#fed7aa",
  white:"#ffffff", bg:"#f1f5f9", text:"#0f172a", textSub:"#64748b", textMuted:"#94a3b8",
  border:"#e2e8f0", green:"#16a34a", greenLight:"#dcfce7", red:"#ef4444", redLight:"#fef2f2",
  yellow:"#f59e0b", yellowLight:"#fefce8", blue:"#2563eb", blueLight:"#eff6ff",
};

const EMPTY = {
  code:"", description:"", type:"percent", value:"", minOrder:"",
  maxDiscount:"", usageLimit:100, validTill:"", applicableOn:"all",
  isActive:true, scope:"global", restaurant:"",
};

// ── Coupon Modal ─────────────────────────────────
function CouponModal({ coupon, onClose, onSave, restaurants }) {
  const [form,   setForm]   = useState(coupon || EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.code || !form.value || !form.validTill)
      return alert("Code, value aur valid till required hai!");
    if (form.scope === "restaurant" && !form.restaurant)
      return alert("Restaurant-specific coupon ke liye restaurant select karo!");

    setSaving(true);
    const payload = {
      ...form,
      restaurant: form.scope === "restaurant" ? form.restaurant : null,
    };
    let res;
    if (form._id) res = await api.coupons.update(form._id, payload);
    else          res = await api.coupons.create(payload);
    setSaving(false);
    if (res.success) onSave(res.data, !!form._id);
    else alert(res.message || "Error!");
  };

  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:T.white,borderRadius:20,width:"100%",maxWidth:460,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.15)" }}>

        {/* Header */}
        <div style={{ padding:"16px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,background:T.white,zIndex:1 }}>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1rem" }}>
            {form._id ? "Edit Coupon" : "Create Coupon"}
          </div>
          <button onClick={onClose} style={{ background:T.bg,border:"none",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer" }}>
            <X size={15} color={T.textSub}/>
          </button>
        </div>

        <div style={{ padding:18,display:"flex",flexDirection:"column",gap:12 }}>

          {/* ✅ Scope toggle — Global vs Restaurant */}
          <div>
            <label style={{ fontSize:"0.72rem",fontWeight:700,color:T.textMuted,display:"block",marginBottom:7 }}>COUPON TYPE</label>
            <div style={{ display:"flex",gap:8 }}>
              {[
                { val:"global",     label:"Global",      sub:"Sab restaurants",     Icon:Globe },
                { val:"restaurant", label:"Restaurant",  sub:"Ek specific restaurant", Icon:Store },
              ].map(({ val, label, sub, Icon }) => (
                <div key={val} onClick={() => set("scope", val)}
                  style={{ flex:1,border:`1.5px solid ${form.scope===val?T.primary:T.border}`,borderRadius:12,padding:"10px 12px",cursor:"pointer",background:form.scope===val?T.primaryLight:T.white,transition:"all .2s" }}>
                  <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:3 }}>
                    <Icon size={14} color={form.scope===val?T.primary:T.textMuted}/>
                    <span style={{ fontWeight:700,fontSize:"0.82rem",color:form.scope===val?T.primary:T.text }}>{label}</span>
                    {form.scope===val && <Check size={12} color={T.primary} style={{ marginLeft:"auto" }}/>}
                  </div>
                  <div style={{ fontSize:"0.68rem",color:T.textMuted }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ✅ Restaurant select — sirf tab dikhao jab restaurant scope ho */}
          {form.scope === "restaurant" && (
            <div>
              <label style={{ fontSize:"0.72rem",fontWeight:700,color:T.textMuted,display:"block",marginBottom:5 }}>SELECT RESTAURANT *</label>
              <select value={form.restaurant} onChange={e => set("restaurant", e.target.value)}
                style={{ width:"100%",border:`1.5px solid ${form.restaurant?T.primary:T.border}`,borderRadius:10,padding:"9px 12px",fontSize:"0.85rem",fontFamily:"inherit",color:T.text,background:T.white }}>
                <option value="">-- Restaurant chunno --</option>
                {restaurants.map(r => (
                  <option key={r._id} value={r._id}>{r.name} — {r.area}</option>
                ))}
              </select>
            </div>
          )}

          {/* Basic fields */}
          {[
            { label:"Coupon Code *",   key:"code",        type:"text",   placeholder:"SAVE50"    },
            { label:"Description",     key:"description", type:"text",   placeholder:"Flat 50 off on all orders" },
            { label:"Discount Value *",key:"value",       type:"number", placeholder:"50"        },
            { label:"Min Order (Rs)",  key:"minOrder",    type:"number", placeholder:"199"       },
            { label:"Max Discount",    key:"maxDiscount", type:"number", placeholder:"100"       },
            { label:"Usage Limit",     key:"usageLimit",  type:"number", placeholder:"100"       },
            { label:"Valid Till *",    key:"validTill",   type:"date",   placeholder:""          },
          ].map(f => (
            <div key={f.key}>
              <label style={{ fontSize:"0.72rem",fontWeight:700,color:T.textMuted,display:"block",marginBottom:5 }}>{f.label}</label>
              <input value={form[f.key]||""} onChange={e => set(f.key, e.target.value)}
                type={f.type} placeholder={f.placeholder}
                style={{ width:"100%",border:`1.5px solid ${T.border}`,borderRadius:10,padding:"9px 12px",fontSize:"0.85rem",fontFamily:"inherit",color:T.text,outline:"none",boxSizing:"border-box" }}/>
            </div>
          ))}

          {/* Discount Type */}
          <div>
            <label style={{ fontSize:"0.72rem",fontWeight:700,color:T.textMuted,display:"block",marginBottom:5 }}>DISCOUNT TYPE</label>
            <select value={form.type} onChange={e => set("type", e.target.value)}
              style={{ width:"100%",border:`1.5px solid ${T.border}`,borderRadius:10,padding:"9px 12px",fontSize:"0.85rem",fontFamily:"inherit",color:T.text,background:T.white }}>
              <option value="percent">Percentage (%)</option>
              <option value="flat">Flat (Rs.)</option>
              <option value="free_delivery">Free Delivery</option>
            </select>
          </div>

          {/* Applicable On */}
          <div>
            <label style={{ fontSize:"0.72rem",fontWeight:700,color:T.textMuted,display:"block",marginBottom:5 }}>APPLICABLE ON</label>
            <select value={form.applicableOn} onChange={e => set("applicableOn", e.target.value)}
              style={{ width:"100%",border:`1.5px solid ${T.border}`,borderRadius:10,padding:"9px 12px",fontSize:"0.85rem",fontFamily:"inherit",color:T.text,background:T.white }}>
              <option value="all">All Services</option>
              <option value="food">Food Only</option>
              <option value="grocery">Grocery Only</option>
            </select>
          </div>

          <button onClick={handleSave} disabled={saving}
            style={{ background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`,border:"none",borderRadius:12,padding:"11px",color:"#fff",fontWeight:800,fontSize:"0.88rem",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:4 }}>
            {saving ? <Loader2 size={16} className="spin"/> : <Check size={16}/>}
            {form._id ? "Save Changes" : "Create Coupon"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────
export default function Coupons() {
  const [coupons,     setCoupons]     = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(null);
  const [toast,       setToast]       = useState("");
  const [filterScope, setFilterScope] = useState("all"); // all | global | restaurant
  const [filterRest,  setFilterRest]  = useState("");

  const showToast = m => { setToast(m); setTimeout(() => setToast(""), 2200); };

  const load = async () => {
    setLoading(true);
    try {
      const [couponRes, restRes] = await Promise.all([
        api.coupons.getAll(),
        api.restaurants.getAll(),
      ]);
      if (couponRes.success) setCoupons(couponRes.data);
      if (restRes.success)   setRestaurants(restRes.data);
    } catch { showToast("Backend connect nahi hua"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (id) => {
    const res = await api.coupons.toggle(id);
    if (res.success) { setCoupons(c => c.map(x => x._id===id ? res.data : x)); showToast("Updated!"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    const res = await api.coupons.delete(id);
    if (res.success) { setCoupons(c => c.filter(x => x._id!==id)); showToast("Deleted!"); }
  };

  const handleSave = (data, isEdit) => {
    if (isEdit) setCoupons(c => c.map(x => x._id===data._id ? data : x));
    else        setCoupons(c => [data, ...c]);
    setModal(null);
    showToast(isEdit ? "Coupon updated!" : "Coupon created!");
  };

  // ✅ Filter logic
  const filtered = coupons.filter(c => {
    if (filterScope === "global"     && c.scope !== "global")     return false;
    if (filterScope === "restaurant" && c.scope !== "restaurant") return false;
    if (filterRest && c.restaurant?._id !== filterRest)           return false;
    return true;
  });

  const TYPE_COLORS = {
    percent:       { c:T.primary, bg:T.primaryLight },
    flat:          { c:T.green,   bg:T.greenLight    },
    free_delivery: { c:T.blue,    bg:T.blueLight     },
  };

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} @keyframes spin{to{transform:rotate(360deg)}}.spin{animation:spin 1s linear infinite}`}</style>

      {toast && (
        <div style={{ position:"fixed",top:80,left:"50%",transform:"translateX(-50%)",background:T.text,color:"#fff",padding:"10px 20px",borderRadius:100,fontSize:"0.82rem",fontWeight:600,zIndex:500,display:"flex",alignItems:"center",gap:7 }}>
          <Check size={14} color={T.green}/> {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:12 }}>
        <div>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1.1rem",color:T.text }}>Coupons & Offers</div>
          <div style={{ fontSize:"0.72rem",color:T.textMuted,marginTop:2 }}>{coupons.filter(c=>c.isActive).length} active · {coupons.length} total</div>
        </div>
        <div style={{ display:"flex",gap:8 }}>
          <button onClick={load} style={{ background:T.white,border:`1px solid ${T.border}`,borderRadius:10,padding:"8px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:"0.78rem",fontWeight:600,color:T.textSub,fontFamily:"inherit" }}>
            <RefreshCw size={14} className={loading?"spin":""}/> Refresh
          </button>
          <button onClick={() => setModal("add")} style={{ background:T.primary,border:"none",borderRadius:10,padding:"9px 18px",fontWeight:700,fontSize:"0.85rem",color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontFamily:"inherit" }}>
            <Plus size={16}/> Create Coupon
          </button>
        </div>
      </div>

      {/* ✅ Filter bar */}
      <div style={{ display:"flex",gap:8,marginBottom:16,flexWrap:"wrap" }}>
        {/* Scope filter */}
        <div style={{ display:"flex",background:T.white,border:`1px solid ${T.border}`,borderRadius:10,padding:3,gap:2 }}>
          {[["all","All"],["global","Global"],["restaurant","Restaurant"]].map(([val,label]) => (
            <button key={val} onClick={() => { setFilterScope(val); setFilterRest(""); }}
              style={{ background:filterScope===val?T.primary:"none",color:filterScope===val?"#fff":T.textSub,border:"none",borderRadius:8,padding:"5px 12px",fontWeight:600,fontSize:"0.75rem",cursor:"pointer",fontFamily:"inherit",transition:"all .2s" }}>
              {label}
            </button>
          ))}
        </div>

        {/* Restaurant filter */}
        {filterScope !== "global" && restaurants.length > 0 && (
          <select value={filterRest} onChange={e => setFilterRest(e.target.value)}
            style={{ border:`1px solid ${T.border}`,borderRadius:10,padding:"6px 12px",fontSize:"0.78rem",fontFamily:"inherit",color:T.text,background:T.white,cursor:"pointer" }}>
            <option value="">All Restaurants</option>
            {restaurants.map(r => (
              <option key={r._id} value={r._id}>{r.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Coupon cards */}
      {loading ? (
        <div style={{ textAlign:"center",padding:60,color:T.textMuted }}>Loading coupons...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center",padding:60 }}>
          <Tag size={44} color={T.border} style={{ marginBottom:12 }}/>
          <div style={{ color:T.textMuted,fontWeight:600 }}>Koi coupons nahi — Create karo!</div>
        </div>
      ) : (
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14 }}>
          {filtered.map((c, i) => {
            const tc      = TYPE_COLORS[c.type] || TYPE_COLORS.percent;
            const expired = new Date(c.validTill) < new Date();
            const restName = c.restaurant?.name;
            return (
              <div key={c._id} style={{ background:T.white,borderRadius:16,border:`1.5px solid ${c.isActive&&!expired?tc.bg:T.border}`,padding:"18px",animation:`fadeUp .3s ${i*.05}s ease both`,opacity:expired?0.6:1 }}>

                {/* Scope badge */}
                <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:8 }}>
                  {c.scope === "restaurant" ? (
                    <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:T.blueLight,color:T.blue,fontSize:"0.6rem",fontWeight:700,padding:"2px 8px",borderRadius:100 }}>
                      <Store size={9}/> {restName || "Restaurant"}
                    </span>
                  ) : (
                    <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:T.primaryLight,color:T.primary,fontSize:"0.6rem",fontWeight:700,padding:"2px 8px",borderRadius:100 }}>
                      <Globe size={9}/> Global
                    </span>
                  )}
                  <span style={{ background:tc.bg,color:tc.c,fontSize:"0.6rem",fontWeight:700,padding:"2px 7px",borderRadius:100,textTransform:"uppercase" }}>
                    {c.type==="free_delivery"?"Free Del":c.type}
                  </span>
                </div>

                <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10 }}>
                  <div>
                    <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1.1rem",color:T.text,letterSpacing:"1px" }}>{c.code}</div>
                    <div style={{ fontSize:"0.75rem",color:T.textSub,marginTop:2 }}>{c.description||"—"}</div>
                  </div>
                  <div onClick={() => handleToggle(c._id)} style={{ cursor:"pointer",color:c.isActive?T.primary:T.textMuted,flexShrink:0 }}>
                    {c.isActive ? <ToggleRight size={24}/> : <ToggleLeft size={24}/>}
                  </div>
                </div>

                <div style={{ display:"flex",gap:8,marginBottom:12 }}>
                  <div style={{ flex:1,background:T.bg,borderRadius:8,padding:"8px",textAlign:"center" }}>
                    <div style={{ fontWeight:800,fontSize:"0.95rem",color:tc.c }}>
                      {c.type==="percent"?`${c.value}%`:c.type==="flat"?`Rs.${c.value}`:"Free"}
                    </div>
                    <div style={{ fontSize:"0.6rem",color:T.textMuted }}>Discount</div>
                  </div>
                  <div style={{ flex:1,background:T.bg,borderRadius:8,padding:"8px",textAlign:"center" }}>
                    <div style={{ fontWeight:800,fontSize:"0.95rem",color:T.text }}>{c.usedCount||0}/{c.usageLimit}</div>
                    <div style={{ fontSize:"0.6rem",color:T.textMuted }}>Used</div>
                  </div>
                  <div style={{ flex:1,background:expired?T.redLight:T.greenLight,borderRadius:8,padding:"8px",textAlign:"center" }}>
                    <div style={{ fontWeight:700,fontSize:"0.7rem",color:expired?T.red:T.green }}>{expired?"Expired":"Active"}</div>
                    <div style={{ fontSize:"0.58rem",color:T.textMuted }}>{new Date(c.validTill).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}</div>
                  </div>
                </div>

                {c.minOrder > 0 && (
                  <div style={{ fontSize:"0.72rem",color:T.textMuted,marginBottom:10 }}>Min order: Rs.{c.minOrder} · {c.applicableOn}</div>
                )}

                <div style={{ display:"flex",gap:8 }}>
                  <button onClick={() => setModal(c)} style={{ flex:1,background:T.primaryLight,border:"none",borderRadius:9,padding:"8px",fontWeight:700,fontSize:"0.78rem",cursor:"pointer",color:T.primary,fontFamily:"inherit" }}>Edit</button>
                  <button onClick={() => handleDelete(c._id)} style={{ flex:1,background:T.redLight,border:"none",borderRadius:9,padding:"8px",fontWeight:700,fontSize:"0.78rem",cursor:"pointer",color:T.red,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4 }}>
                    <Trash2 size={12}/> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <CouponModal
          coupon={modal==="add" ? null : modal}
          restaurants={restaurants}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}